// Administration → Données (docs/wip/SPEC-MCP.md §4 and §6; only when the server reports
// features.mcp): the Tables du bureau of each floor (GET /api/tables), a preview of the rows of a
// table, 200 at a time (GET /api/tables/:floorId/:table?limit=200&offset=…: the first ones, then
// « 200 suivantes » to reach the rows agents just added), where a cell is edited in place (click →
// a field of the column's type; Enter or leaving the field saves with PATCH
// /api/tables/:floorId/:table/rows/:rowId {set}, Escape cancels), a row is added through a small
// form (POST /api/tables/:floorId/:table/rows {rows: [row]}), rows can be selected and deleted
// (POST /api/tables/:floorId/:table/delete-rows {ids}), and « Supprimer la table » (DELETE
// /api/tables/:floorId/:table), confirmed by typing the table's name. « Nouvelle table » creates
// one on the floor shown (POST /api/tables/:floorId {table, description, key?, columns?}).
// A floor that uses another floor's data (« Données de l’étage », `dataFloor`) has no tables of its
// own: it says whose tables its agents use, with a link to them, and no « Nouvelle table »; the
// host says which floors share its tables (and their limit).
// Made to be found (SPEC-UX-V03 §F): each table of the list has « Ouvrir et modifier » (and its
// name opens it too), « + Ligne », and « Supprimer la table » in a ⋯ menu; the preview has a
// toolbar above the grid (« + Ajouter une ligne », « Supprimer la sélection (n) » only with a
// selection), a help banner, a pencil on the hovered cell, a last line « + Nouvelle ligne… », and
// the edited cell keeps its column's width.
// Rows are written by agents from outside sources (TrendTrack, ClickUp…): every value is shown as
// plain text, never as markup or a link.
import { h, button, iconButton, input, textarea, select, field, emptyState, icon } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime, formatNumber, formatBytes, truncate } from '../../format.js';
import { sortedFloors } from '../../store.js';
import { errorText } from './model.js';
import { sectionHead, loadingState, errorState, formError, setBusy, focusKeeper, aid } from './common.js';
import './strings.js';

/** Rows shown per page of a preview (the server reads 500 at most per call). */
export const PREVIEW_ROWS = 200;
/** A cell longer than this is cut (the full text stays in its tooltip). */
export const CELL_MAX = 160;
/** A text cell longer than this (or on several lines) is edited in a textarea. */
export const LONG_TEXT = 80;
/** Longest text value the server accepts (TABLE_LIMITS.textValue). */
const TEXT_MAX = 4000;
/** Names the server accepts (server/tables/service.js TABLE_NAME_RE, COLUMN_NAME_RE). */
export const TABLE_NAME_RE = /^[a-z0-9-]{1,60}$/;
export const COLUMN_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{0,62}$/;
/** Longest table description, most declared columns (TABLE_LIMITS). */
const DESCRIPTION_MAX = 500;
const COLUMNS_MAX = 50;
/** Several table events in a row (an agent inserting, then updating) give one reload. */
const RELOAD_DEBOUNCE_MS = 500;
/** Relative times ("il y a 5 min") are refreshed this often. */
const CLOCK_MS = 60_000;

const enc = encodeURIComponent;

/**
 * A size limit in words: whole megabytes without a decimal (« 2 Mo », not « 2,0 Mo »).
 * @param {number} bytes
 * @returns {string}
 */
export function limitSize(bytes) {
  const mb = Number(bytes) / (1024 * 1024);
  return Number.isInteger(mb) && mb > 0 ? `${formatNumber(mb)} Mo` : formatBytes(bytes);
}

/**
 * Normalises a GET /api/tables response (`{floors: [{id, name, dataFloor?, tables}], limits}`),
 * floors in the elevator order of the office when it is known. `dataFloor`: the floor whose
 * tables a floor uses (« Données de l’étage »), null for its own; such a floor lists no table.
 * @param {unknown} res
 * @param {object} [office] the store's office (floors with their order)
 * @returns {{floors: Array<{id: string, name: string, dataFloor: string|null, tables: object[]}>, limits: object|null}}
 */
export function normalizeTables(res, office) {
  const r = res && typeof res === 'object' ? res : {};
  const order = new Map(sortedFloors(office?.floors || []).map((f, i) => [f.id, i]));
  const floors = (Array.isArray(r.floors) ? r.floors : [])
    .filter((f) => f && typeof f === 'object' && typeof f.id === 'string' && f.id)
    .map((f, i) => ({
      id: f.id,
      name: String(f.name || f.id),
      dataFloor: typeof f.dataFloor === 'string' && f.dataFloor && f.dataFloor !== f.id ? f.dataFloor : null,
      tables: (f.dataFloor && f.dataFloor !== f.id ? [] : Array.isArray(f.tables) ? f.tables : [])
        .filter((x) => x && typeof x === 'object' && typeof x.name === 'string' && x.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
      rank: order.has(f.id) ? order.get(f.id) : order.size + i,
    }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ rank, ...f }) => f);
  return { floors, limits: r.limits && typeof r.limits === 'object' ? r.limits : null };
}

/**
 * The floor to show: `current` when it still exists, else the first floor that has tables, else
 * the first floor with its own data (one that uses another floor's data only shows a link to
 * them), else the first floor (null without floors).
 * @param {Array<{id: string, dataFloor?: string|null, tables: object[]}>} floors
 * @param {string|null} current
 */
export function pickFloor(floors, current) {
  const list = floors || [];
  if (current && list.some((f) => f.id === current)) return current;
  return (list.find((f) => f.tables.length) || list.find((f) => !f.dataFloor) || list[0])?.id || null;
}

/**
 * Columns of a preview: the table's declared columns, then any other column found in the rows
 * (a table created without columns); the system columns (_id, _createdAt, _updatedAt, _by) are
 * left out — they start with "_", which a column name never does.
 * @param {{columns?: Array<{name: string}|string>, rows?: object[]}} page
 * @returns {string[]}
 */
export function previewColumns(page) {
  const out = [];
  const seen = new Set();
  const add = (name) => {
    if (typeof name !== 'string' || !name || name.startsWith('_') || seen.has(name)) return;
    seen.add(name);
    out.push(name);
  };
  for (const c of Array.isArray(page?.columns) ? page.columns : []) add(typeof c === 'string' ? c : c?.name);
  for (const row of Array.isArray(page?.rows) ? page.rows : []) {
    if (row && typeof row === 'object') for (const k of Object.keys(row)) add(k);
  }
  return out;
}

/**
 * Text of a cell: "—" when empty, "oui" / "non" for booleans, numbers as written (identifiers
 * must not get thousands separators), anything else as a string.
 * @param {unknown} v
 */
export function cellText(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? t('adm.data.yes') : t('adm.data.no');
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return '…'; }
  }
  return String(v);
}

/**
 * Who wrote a row (`_by`): the agent's name (or id), "vous" or "Administrateur".
 * @param {{agentId?: string, taskId?: string, userId?: string}|null} by
 * @param {{office?: object, me?: object}} [state]
 */
export function rowAuthor(by, { office, me } = {}) {
  if (!by || typeof by !== 'object') return '—';
  if (typeof by.agentId === 'string' && by.agentId) {
    const agent = (office?.agents || []).find((a) => a.id === by.agentId);
    return agent?.name || by.agentId;
  }
  if (typeof by.userId === 'string' && by.userId) return by.userId === me?.id ? t('adm.you') : t('adm.data.by.admin');
  return '—';
}

/**
 * The declared type of each column of a page ('text' | 'number' | 'boolean'; untyped columns are
 * left out).
 * @param {{columns?: Array<{name: string, type?: string}|string>}} page
 * @returns {Map<string, string>}
 */
export function columnTypes(page) {
  const out = new Map();
  for (const c of Array.isArray(page?.columns) ? page.columns : []) {
    if (c && typeof c === 'object' && typeof c.name === 'string' && ['text', 'number', 'boolean'].includes(c.type)) out.set(c.name, c.type);
  }
  return out;
}

/**
 * The kind of field that edits a cell: the column's declared type; for an untyped column, the type
 * of the current value, else of the first filled cell of that column in `rows`, else text.
 * @param {string|undefined} type declared type
 * @param {unknown} value current value (undefined for a new row)
 * @param {object[]} [rows] rows of the page (to guess an untyped column)
 * @param {string} [col]
 * @returns {'text'|'number'|'boolean'}
 */
export function cellKind(type, value, rows = [], col = '') {
  if (type === 'text' || type === 'number' || type === 'boolean') return type;
  const kindOf = (v) => (typeof v === 'boolean' ? 'boolean' : typeof v === 'number' ? 'number' : 'text');
  if (value !== null && value !== undefined) return kindOf(value);
  const seen = (rows || []).find((r) => r && typeof r === 'object' && r[col] !== null && r[col] !== undefined);
  return seen ? kindOf(seen[col]) : 'text';
}

/**
 * The value typed in a cell field. Empty → null; booleans come from a select ('true' / 'false' /
 * ''); a number accepts a decimal comma and spaces (« 1 234,5 »). In an untyped column, text that
 * is not a number stays text; in a number column it is refused.
 * @param {unknown} raw the field's value
 * @param {'text'|'number'|'boolean'} kind
 * @param {string} [type] declared type of the column
 * @returns {{value: string|number|boolean|null}|{error: string}}
 */
export function parseCell(raw, kind, type) {
  const s = String(raw ?? '');
  if (kind === 'boolean') return { value: s === 'true' ? true : s === 'false' ? false : null };
  if (!s.trim()) return { value: null };
  if (kind === 'number') {
    const compact = s.trim().replace(/[\s  ]/g, '').replace(',', '.');
    const n = /\d/.test(compact) ? Number(compact) : NaN;
    if (Number.isFinite(n)) return { value: n };
    if (type === 'number') return { error: t('adm.data.cell.number') };
    return { value: s };
  }
  if (s.length > TEXT_MAX) return { error: t('adm.data.cell.tooLong', { max: formatNumber(TEXT_MAX) }) };
  return { value: s };
}

/**
 * Checks the « Nouvelle table » form and builds the body of POST /api/tables/:floorId (pure).
 * Columns with an empty name are ignored; `key` must be one of the declared columns when there are
 * some (the server says the same).
 * @param {{table: string, description: string, key?: string, columns?: Array<{name: string, type?: string}>}} form
 * @param {{taken?: string[]}} [opts] names of the floor's tables
 * @returns {{body: object|null, errors: {table?: string, description?: string, columns?: string, key?: string}}}
 */
export function newTableBody(form, { taken = [] } = {}) {
  const errors = {};
  const table = String(form?.table ?? '').trim();
  if (!table) errors.table = t('common.required');
  else if (!TABLE_NAME_RE.test(table)) errors.table = t('adm.data.new.nameInvalid');
  else if (taken.includes(table)) errors.table = t('adm.data.new.nameTaken');
  const description = String(form?.description ?? '').replace(/\s+/g, ' ').trim();
  if (!description) errors.description = t('common.required');
  else if (description.length > DESCRIPTION_MAX) errors.description = t('adm.data.cell.tooLong', { max: formatNumber(DESCRIPTION_MAX) });
  const columns = [];
  const seen = new Set();
  (Array.isArray(form?.columns) ? form.columns : []).forEach((c, i) => {
    const name = String(c?.name ?? '').trim();
    if (!name || errors.columns) return;
    if (!COLUMN_NAME_RE.test(name)) errors.columns = t('adm.data.new.columnInvalid', { n: i + 1 });
    else if (seen.has(name.toLowerCase())) errors.columns = t('adm.data.new.columnDuplicate', { name }); // « Site » = « site », as on the server
    else {
      seen.add(name.toLowerCase());
      columns.push({ name, type: ['text', 'number', 'boolean'].includes(c.type) ? c.type : 'text' });
    }
  });
  if (!errors.columns && columns.length > COLUMNS_MAX) errors.columns = t('adm.data.new.columnsMax', { max: COLUMNS_MAX });
  const key = String(form?.key ?? '').trim();
  if (key && columns.length && !columns.some((c) => c.name === key)) errors.key = t('adm.data.new.keyHint');
  if (Object.keys(errors).length) return { body: null, errors };
  const body = { table, description };
  if (key) body.key = key;
  if (columns.length) body.columns = columns;
  return { body, errors };
}

/** Same cell value (a missing cell is null). */
function sameCell(a, b) {
  return (a ?? null) === (b ?? null);
}

/**
 * Creates the « Données » tab.
 * @param {object} ctx app context
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createDataView(ctx) {
  let data = null;           // normalizeTables()
  let error = null;
  let floorId = null;
  let destroyed = false;
  let reloadTimer = null;
  let preview = null;        // the open preview: {layer, floorId, table, reload()}
  let dropLayer = null;      // the « Supprimer la table » form, while open
  let addLayer = null;       // the « Ajouter une ligne » form, while open
  let newLayer = null;       // the « Nouvelle table » form, while open
  let floorOptions = '';     // what the floor select lists (rebuilt only when it changes)
  const keeper = focusKeeper();

  const floorSel = select([], { class: 'input-sm adm-data-floor', 'aria-label': t('adm.data.floor') });
  floorSel.addEventListener('change', () => { floorId = floorSel.value; render(); });
  const refreshBtn = button(t('adm.refresh'), { variant: 'secondary', icon: 'refresh', onClick: () => load() });
  const newBtn = button(t('adm.data.new'), { variant: 'primary', icon: 'plus', disabled: true, onClick: () => openNewTable() });
  const head = sectionHead({
    title: t('adm.data.title'), intro: t('adm.data.intro'),
    actions: [h('label', { class: 'adm-filter adm-data-floor-filter' }, h('span', { class: 'mono-label' }, t('adm.data.floor')), floorSel), refreshBtn, newBtn],
  });
  const limitsEl = h('p', { class: 'adm-data-limits muted', hidden: true });
  const body = h('div', { class: 'adm-body' }, loadingState());
  const el = h('section', { class: 'adm-view adm-data' }, head, limitsEl, body);

  async function load({ quiet = false } = {}) {
    refreshBtn.disabled = true;
    try {
      const res = await ctx.api.get('/api/tables');
      if (destroyed) return;
      data = normalizeTables(res, ctx.store.get().office);
      error = null;
    } catch (err) {
      if (destroyed) return;
      if (!quiet || !data) error = err;
    } finally {
      if (!destroyed) refreshBtn.disabled = false;
    }
    render();
  }

  /**
   * Reloads the list (and the open preview) shortly after a burst of table events. While another
   * tab of the sheet is shown, the list waits: coming back to this tab reloads it anyway.
   */
  function scheduleReload() {
    if (destroyed) return;
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadTimer = null;
      if (destroyed) return;
      if (!el.parentNode?.hidden) load({ quiet: true });
      preview?.reload();
    }, RELOAD_DEBOUNCE_MS);
  }

  function currentFloor() {
    return data?.floors.find((f) => f.id === floorId) || null;
  }

  // --- rendering --------------------------------------------------------------------------------
  function render() {
    const restore = keeper.begin();
    if (error && !data) {
      head.setCount('');
      floorSel.disabled = true;
      newBtn.disabled = true;
      refreshBtn.disabled = error.status === 404;
      limitsEl.hidden = true;
      body.replaceChildren(errorState(error, {
        onRetry: () => { body.replaceChildren(loadingState()); load(); }, unavailableText: t('adm.data.unavailable'),
      }));
      return;
    }
    if (!data) return;
    const l = data.limits;
    limitsEl.hidden = !l;
    limitsEl.textContent = l ? t('adm.data.limits', {
      tables: formatNumber(l.tablesPerFloor), rows: formatNumber(l.rowsPerTable), size: limitSize(l.bytesPerTable),
    }) : '';
    floorId = pickFloor(data.floors, floorId);
    // Options are rebuilt only when they change (a quiet reload must not close an open list).
    const nameOf = (id) => data.floors.find((x) => x.id === id)?.name || id;
    const options = data.floors.map((f) => [f.id, f.dataFloor
      ? t('adm.data.floorOptionGuest', { name: f.name, host: nameOf(f.dataFloor) })
      : t('adm.data.floorOption', { name: f.name, n: f.tables.length })]);
    const signature = JSON.stringify(options);
    if (floorOptions !== signature) {
      floorSel.replaceChildren(...options.map(([value, label]) => h('option', { value }, label)));
      floorOptions = signature;
    }
    floorSel.disabled = !data.floors.length;
    if (floorId) floorSel.value = floorId;
    const floor = currentFloor();
    newBtn.disabled = !floor || Boolean(floor.dataFloor);
    newBtn.hidden = Boolean(floor?.dataFloor);
    if (!floor) {
      head.setCount('');
      body.replaceChildren(emptyState({ icon: 'building', title: t('adm.data.noFloors.title'), text: t('adm.data.noFloors.text') }));
      restore();
      return;
    }
    // A floor that uses another floor's data: whose tables they are, and a link to them.
    if (floor.dataFloor) {
      const host = data.floors.find((x) => x.id === floor.dataFloor) || null;
      const vars = { name: floor.name, host: nameOf(floor.dataFloor) };
      head.setCount('');
      body.replaceChildren(emptyState({
        icon: 'layers', title: t('adm.data.guest.title', vars), text: t('adm.data.guest.text', vars),
        action: host ? keeper.reg(`host:${host.id}`, button(t('adm.data.guest.open', vars), {
          variant: 'secondary', iconRight: 'arrowRight', class: 'adm-data-host',
          onClick: () => { floorId = host.id; render(); floorSel.focus?.(); },
        })) : null,
      }));
      restore();
      return;
    }
    head.setCount(t('adm.data.count', { n: floor.tables.length }));
    // A host: the floors that use its tables too (the limit counts them all).
    const guests = data.floors.filter((x) => x.dataFloor === floor.id);
    const shared = guests.length
      ? h('p', { class: 'adm-note adm-data-shared' }, t('adm.data.sharedWith', { n: guests.length, list: guests.map((g) => `« ${g.name} »`).join(', ') }))
      : null;
    if (!floor.tables.length) {
      body.replaceChildren(...[shared, emptyState({ icon: 'list', title: t('adm.data.empty.title'), text: t('adm.data.empty.text') })].filter(Boolean));
      restore();
      return;
    }
    const now = Date.now();
    const th = (key) => h('th', { scope: 'col' }, t(`adm.data.col.${key}`));
    body.replaceChildren(...[shared].filter(Boolean), h('div', { class: 'adm-table-wrap' },
      h('table', { class: 'table adm-table adm-data-table' },
        h('thead', {}, h('tr', {}, th('name'), th('description'), th('key'), th('rows'), th('updated'),
          h('th', { scope: 'col' }, h('span', { class: 'sr-only' }, t('adm.actions'))))),
        h('tbody', {}, floor.tables.map((tb) => tableRow(floor, tb, now))))));
    restore();
  }

  function tableRow(floor, tb, now) {
    const open = keeper.reg(`open:${floor.id}/${tb.name}`, button(t('adm.data.open'), {
      variant: 'secondary', size: 'sm', icon: 'pencil', ariaLabel: t('adm.data.openAria', { name: tb.name }), onClick: () => openPreview(floor, tb),
    }));
    const add = keeper.reg(`add:${floor.id}/${tb.name}`, button(t('adm.data.addRowShort'), {
      variant: 'ghost', size: 'sm', icon: 'plus', ariaLabel: t('adm.data.addRowAria', { name: tb.name }), onClick: () => openPreview(floor, tb, { addRow: true }),
    }));
    const more = keeper.reg(`more:${floor.id}/${tb.name}`, iconButton('dots', t('adm.data.more', { name: tb.name }), {
      variant: 'ghost', onClick: () => ctx.overlay.menu?.(more, [
        { label: t('adm.data.drop'), icon: 'close', danger: true, onSelect: () => openDrop(floor, tb) },
      ], { placement: 'bottom-end', label: t('adm.data.more', { name: tb.name }) }),
    }));
    more.setAttribute('aria-haspopup', 'menu');
    const name = keeper.reg(`name:${floor.id}/${tb.name}`, h('button', {
      type: 'button', class: 'link-btn adm-data-open', title: t('adm.data.openAria', { name: tb.name }), onClick: () => openPreview(floor, tb),
    }, h('code', { class: 'adm-data-name' }, tb.name)));
    return h('tr', { class: 'adm-data-row', dataset: { table: tb.name } },
      h('td', { class: 'adm-cell-main', 'data-label': t('adm.data.col.name') }, name),
      h('td', { class: 'adm-data-desc', 'data-label': t('adm.data.col.description'), title: tb.description || null },
        tb.description ? truncate(tb.description, 200) : h('span', { class: 'muted' }, '—')),
      h('td', { 'data-label': t('adm.data.col.key') },
        tb.key ? h('code', { class: 'adm-code', title: t('adm.data.keyTitle') }, tb.key) : h('span', { class: 'muted' }, '—')),
      h('td', { 'data-label': t('adm.data.col.rows'), title: Number.isFinite(tb.size) ? t('adm.data.size', { size: formatBytes(tb.size) }) : null },
        formatNumber(tb.rows ?? 0)),
      h('td', { 'data-label': t('adm.data.col.updated') },
        tb.updatedAt ? h('time', { datetime: String(tb.updatedAt), title: formatDateTime(tb.updatedAt) }, relativeTime(tb.updatedAt, now)) : h('span', { class: 'muted' }, '—')),
      h('td', { class: 'adm-cell-actions' }, h('span', { class: 'adm-data-actions' }, open, add, more)));
  }

  // --- preview (a page of rows: edit a cell, add a row, delete a selection) ---------------------
  /**
   * Opens the rows of a table in a drawer.
   * @param {{addRow?: boolean}} [opts] addRow: open « Ajouter une ligne » once the rows are there
   */
  function openPreview(floor, tb, { addRow = false } = {}) {
    preview?.layer?.close('replace');
    let wantAddRow = addRow;
    let page = null;
    let pageError = null;
    let seq = 0;
    let offset = 0;             // first row of the page (the server pages with offset)
    const selected = new Set();
    // The grid is rebuilt only when rows are loaded: ticking a row updates it in place, and a
    // reload (an agent wrote, rows deleted) keeps the scroll position and the focused checkbox.
    const gridKeeper = focusKeeper();
    let gridWrap = null;
    let allBox = null;
    let lines = new Map();      // _id → {tr, box}
    let types = new Map();      // column → declared type
    let editing = null;         // the open cell editor: {id, col, cancel({focus})}
    let pendingRender = false;  // rows loaded while a cell was being edited: shown once it closes
    const note = h('p', { class: 'adm-data-status muted', 'aria-live': 'polite' });
    const said = h('p', { class: 'sr-only', 'aria-live': 'polite' }); // « Cellule enregistrée. »
    const prevBtn = button(t('adm.data.preview.prev', { n: PREVIEW_ROWS }), {
      variant: 'secondary', size: 'sm', icon: 'chevronLeft', onClick: () => turn(-1),
    });
    const nextBtn = button(t('adm.data.preview.next', { n: PREVIEW_ROWS }), {
      variant: 'secondary', size: 'sm', iconRight: 'chevronRight', onClick: () => turn(1),
    });
    const pager = h('div', { class: 'adm-data-pager', hidden: true }, prevBtn, nextBtn);
    const content = h('div', { class: 'adm-data-preview' }, loadingState());
    const deleteBtn = button(t('adm.data.deleteRows'), { variant: 'danger', size: 'sm', onClick: () => deleteSelected() });
    deleteBtn.hidden = true; // only with a selection
    const dropBtn = button(t('adm.data.drop'), { variant: 'ghost', size: 'sm', class: 'adm-del', ariaLabel: t('adm.data.dropAria', { name: tb.name }), onClick: () => openDrop(floor, tb) });
    const addBtn = button(t('adm.data.addRow'), { variant: 'primary', size: 'sm', icon: 'plus', disabled: true, onClick: () => openAddRow() });
    // The row actions sit above the grid, where the eye already is.
    const toolbar = h('div', { class: 'adm-data-toolbar', role: 'toolbar', 'aria-label': t('adm.data.toolbar') }, addBtn, deleteBtn);
    const layer = ctx.overlay.openDrawer({
      title: tb.name, subtitle: floor.name, size: 'xl', content: [note, said, toolbar, pager, content],
      footer: [dropBtn],
      className: 'adm-data-drawer',
    });
    const p = { layer, floorId: floor.id, table: tb.name, reload: () => loadPage({ keepScroll: true }) };
    preview = p;
    layer.onClose?.(() => {
      editing?.cancel({ focus: false });
      addLayer?.close?.('replace');
      if (preview === p) preview = null;
    });
    const rowsPath = `/api/tables/${enc(floor.id)}/${enc(tb.name)}/rows`;

    async function loadPage({ keepScroll = false } = {}) {
      const mine = ++seq;
      try {
        const res = await ctx.api.get(ctx.api.withQuery(`/api/tables/${enc(floor.id)}/${enc(tb.name)}`, { limit: PREVIEW_ROWS, offset: offset || undefined }));
        if (destroyed || layer.closed || mine !== seq) return;
        page = res && typeof res === 'object' ? { ...res, rows: Array.isArray(res.rows) ? res.rows.filter((r) => r && typeof r === 'object') : [] } : { rows: [] };
        pageError = null;
        // Rows deleted from the end: go back to the last page that still has rows.
        const total = Number.isFinite(page.total) ? page.total : page.rows.length;
        if (!page.rows.length && offset > 0 && total > 0) {
          offset = Math.floor((total - 1) / PREVIEW_ROWS) * PREVIEW_ROWS;
          loadPage();
          return;
        }
        // Rows deleted elsewhere (or on another page) leave the selection.
        const ids = new Set(page.rows.map((r) => r._id));
        for (const id of [...selected]) if (!ids.has(id)) selected.delete(id);
      } catch (err) {
        if (destroyed || layer.closed || mine !== seq) return;
        pageError = err;
      }
      // Never rebuild the grid under an open cell editor: the new rows wait until it closes.
      if (editing) {
        pendingRender = true;
        return;
      }
      renderPage({ keepScroll });
    }

    /** Another page of rows (the previous or the next PREVIEW_ROWS). */
    function turn(dir) {
      const total = Number.isFinite(page?.total) ? page.total : 0;
      const next = Math.max(0, Math.min(offset + dir * PREVIEW_ROWS, Math.max(0, total - 1)));
      if (next === offset || !page) return;
      offset = next;
      selected.clear();
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      loadPage();
    }

    function syncDelete() {
      const n = selected.size;
      deleteBtn.hidden = n === 0;
      deleteBtn.disabled = n === 0;
      deleteBtn.lastChild.textContent = n ? t('adm.data.deleteRowsN', { n }) : t('adm.data.deleteRows');
    }

    /** Ticks, row highlights, « select all » and the delete button, from `selected` (no rebuild). */
    function syncSelection() {
      const ids = [...lines.keys()];
      for (const [id, line] of lines) {
        const on = selected.has(id);
        line.box.checked = on;
        line.tr.classList.toggle('is-selected', on);
      }
      if (allBox) {
        allBox.checked = ids.length > 0 && ids.every((id) => selected.has(id));
        allBox.indeterminate = !allBox.checked && ids.some((id) => selected.has(id));
      }
      syncDelete();
    }

    function syncPager(total, shown) {
      pager.hidden = total <= PREVIEW_ROWS;
      prevBtn.disabled = offset <= 0;
      nextBtn.disabled = offset + shown >= total;
      if (offset > 0) {
        note.textContent = t('adm.data.preview.range', { from: formatNumber(offset + 1), to: formatNumber(offset + shown), total: formatNumber(total) });
      } else {
        note.textContent = total > shown ? t('adm.data.preview.first', { shown: formatNumber(shown), total: formatNumber(total) }) : '';
      }
    }

    function renderPage({ keepScroll = false } = {}) {
      pendingRender = false;
      syncDelete();
      addBtn.disabled = !page || Boolean(pageError);
      if (pageError) {
        note.textContent = '';
        pager.hidden = true;
        gridWrap = null;
        allBox = null;
        lines = new Map();
        content.replaceChildren(pageError.status === 404
          ? emptyState({ icon: 'info', title: t('adm.data.preview.gone') })
          : errorState(pageError, { onRetry: () => { content.replaceChildren(loadingState()); loadPage(); } }));
        return;
      }
      if (!page) return;
      const rows = page.rows;
      const total = Number.isFinite(page.total) ? page.total : rows.length;
      layer.setSubtitle?.(`${floor.name} · ${t('adm.data.rows', { n: formatNumber(total), count: total })}`);
      syncPager(total, rows.length);
      const cols = previewColumns(page);
      const key = page.key || tb.key || null;
      types = columnTypes(page);
      const info = h('div', { class: 'adm-data-info' },
        page.description ? h('p', { class: 'adm-data-description' }, page.description) : null,
        h('p', { class: 'adm-data-cols' }, h('span', { class: 'mono-label' }, t('adm.data.preview.columns')), ' ',
          cols.length
            ? cols.map((c) => h('code', { class: ['adm-data-col', { 'is-key': c === key }], title: c === key ? t('adm.data.keyTitle') : null }, c))
            : h('span', { class: 'muted' }, t('adm.data.preview.noColumns'))),
        rows.length && cols.length ? h('p', { class: 'adm-data-help', role: 'note' }, icon('info', { size: 16 }), h('span', {}, t('adm.data.editHint'))) : null);
      if (wantAddRow) {
        wantAddRow = false;
        queueMicrotask(() => { if (!destroyed && !layer.closed && page) openAddRow(); });
      }
      if (!rows.length) {
        gridWrap = null;
        allBox = null;
        lines = new Map();
        content.replaceChildren(info, emptyState({ icon: 'list', title: t('adm.data.preview.empty') }));
        return;
      }
      const restore = gridKeeper.begin();
      const scroll = keepScroll && gridWrap ? { top: gridWrap.scrollTop, left: gridWrap.scrollLeft } : null;
      lines = new Map();
      allBox = gridKeeper.reg('all', h('input', { type: 'checkbox', class: 'adm-check-input', 'aria-label': t('adm.data.selectAll') }));
      allBox.addEventListener('change', () => {
        for (const id of lines.keys()) if (allBox.checked) selected.add(id); else selected.delete(id);
        syncSelection();
      });
      const state = { office: ctx.store.get().office, me: ctx.store.get().me };
      const now = Date.now();
      const headRow = h('tr', {},
        h('th', { scope: 'col', class: 'adm-data-check' }, allBox),
        cols.map((c) => h('th', { scope: 'col', class: { 'is-key': c === key } }, c,
          c === key ? h('span', { class: 'adm-data-keytag mono-label', title: t('adm.data.keyTitle') }, t('adm.data.keyTag')) : null)),
        h('th', { scope: 'col' }, t('adm.data.col.createdAt')),
        h('th', { scope: 'col' }, t('adm.data.col.by')));
      const bodyRows = rows.map((r, i) => {
        const id = typeof r._id === 'string' ? r._id : null;
        const box = id ? gridKeeper.reg(`sel:${id}`, h('input', { type: 'checkbox', class: 'adm-check-input', 'aria-label': t('adm.data.select', { n: offset + i + 1 }) })) : null;
        box?.addEventListener('change', () => {
          if (box.checked) selected.add(id); else selected.delete(id);
          syncSelection();
        });
        const tr = h('tr', { class: 'adm-data-line', dataset: { id: String(r._id ?? '') } },
          h('td', { class: 'adm-data-check' }, box),
          cols.map((c) => cellTd(r, c, id, offset + i + 1, key)),
          h('td', { class: 'adm-data-meta' }, r._createdAt
            ? h('time', { datetime: String(r._createdAt), title: formatDateTime(r._createdAt) }, relativeTime(r._createdAt, now)) : '—'),
          h('td', { class: 'adm-data-meta' }, rowAuthor(r._by, state)));
        if (id) lines.set(id, { tr, box });
        return tr;
      });
      // Last line: « + Nouvelle ligne… » opens the same form as the toolbar.
      const newLine = h('tr', { class: 'adm-data-newline' }, h('td', { colspan: String(cols.length + 3) },
        gridKeeper.reg('newline', h('button', { type: 'button', class: 'adm-data-newline-btn', onClick: () => openAddRow() },
          icon('plus', { size: 14 }), h('span', {}, t('adm.data.newRowLine'))))));
      gridWrap = h('div', { class: 'adm-data-grid-wrap' },
        h('table', { class: 'table adm-data-grid' }, h('thead', {}, headRow), h('tbody', {}, bodyRows, newLine)));
      content.replaceChildren(info, gridWrap);
      syncSelection();
      if (scroll) {
        gridWrap.scrollTop = scroll.top;
        gridWrap.scrollLeft = scroll.left;
      }
      restore();
    }

    // --- a cell edited in place ---------------------------------------------------------------
    function rowById(id) {
      return page?.rows.find((r) => r._id === id) || null;
    }

    /** The button of a cell (its value as text) that opens the editor. */
    function fillCell(cell) {
      const row = rowById(cell.id);
      const text = cellText(row ? row[cell.col] : null);
      cell.td.classList.remove('is-editing');
      cell.td.classList.toggle('is-empty', text === '—');
      if (text.length > CELL_MAX) cell.td.setAttribute('title', text); else cell.td.removeAttribute('title');
      cell.td.style.removeProperty('--cell-w');
      // The value, then a pencil shown on hover (CSS): the cell says it can be edited.
      cell.btn.replaceChildren(h('span', { class: 'adm-data-cell-text' }, truncate(text, CELL_MAX)),
        icon('pencil', { size: 12, class: 'adm-data-cell-pen' }));
      cell.btn.setAttribute('aria-label', t('adm.data.cell.edit', { col: cell.col, n: cell.n, value: truncate(text, 80) }));
      cell.td.replaceChildren(cell.btn);
    }

    function cellTd(r, col, id, n, key) {
      const text = cellText(r[col]);
      const td = h('td', { class: { 'is-empty': text === '—', 'is-key': col === key }, title: text.length > CELL_MAX ? text : null });
      if (!id) {
        td.append(truncate(text, CELL_MAX));
        return td;
      }
      const btn = gridKeeper.reg(`cell:${id}:${col}`, h('button', { type: 'button', class: 'adm-data-cell' }));
      const cell = { td, btn, id, col, n, key };
      btn.addEventListener('click', () => openEditor(cell));
      fillCell(cell);
      return td;
    }

    function openEditor(cell) {
      if (editing) {
        if (editing.saving) return;
        editing.cancel({ focus: false });
      }
      const row = rowById(cell.id);
      if (!row) return;
      const { col } = cell;
      const current = row[col] ?? null;
      const type = types.get(col);
      const kind = cellKind(type, current, page.rows, col);
      const label = t('adm.data.cell.input', { col, n: cell.n });
      const errId = aid('cell-err');
      const err = h('p', { class: 'adm-data-cell-error', id: errId, role: 'alert', hidden: true });
      const common = { class: 'input-sm adm-data-editor', 'aria-label': label, 'aria-describedby': errId };
      let ctl;
      if (kind === 'boolean') {
        ctl = select([['', t('adm.data.bool.empty')], ['true', t('adm.data.yes')], ['false', t('adm.data.no')]],
          { ...common, value: current === true ? 'true' : current === false ? 'false' : '' });
      } else if (kind === 'text' && (String(current ?? '').length > LONG_TEXT || String(current ?? '').includes('\n'))) {
        ctl = textarea({ ...common, rows: 4, maxlength: TEXT_MAX, value: String(current ?? '') });
      } else {
        ctl = input({ ...common, value: current === null ? '' : String(current), autocomplete: 'off', inputmode: kind === 'number' ? 'decimal' : null, maxlength: TEXT_MAX });
      }
      const ed = { id: cell.id, col, saving: false, done: false, failed: null, cancel };
      editing = ed;
      const win = globalThis.window;

      function showError(msg) {
        err.textContent = msg;
        err.hidden = false;
        ctl.setAttribute('aria-invalid', 'true');
        cell.td.classList.add('has-error');
      }

      function close({ focus = true } = {}) {
        if (ed.done) return;
        ed.done = true;
        win?.removeEventListener?.('keydown', onWinKey, true);
        if (editing === ed) editing = null;
        cell.td.classList.remove('has-error', 'is-saving');
        fillCell(cell);
        if (focus && !layer.closed) cell.btn.focus?.();
        if (pendingRender && !destroyed && !layer.closed) renderPage({ keepScroll: true });
      }

      function cancel(opts) {
        close(opts);
      }

      async function save(how) {
        if (ed.done || ed.saving) return;
        const raw = String(ctl.value ?? '');
        if (how === 'blur' && raw === ed.failed) return; // already refused: leaving the field does not resend it
        const parsed = parseCell(raw, kind, type);
        if (parsed.error) {
          ed.failed = raw;
          showError(parsed.error);
          return;
        }
        if (sameCell(parsed.value, current)) {
          close({ focus: how !== 'blur' });
          return;
        }
        if (col === cell.key && parsed.value === null) {
          ed.failed = raw;
          showError(t('adm.data.cell.keyRequired'));
          return;
        }
        ed.saving = true;
        ctl.setAttribute('aria-busy', 'true');
        cell.td.classList.add('is-saving');
        try {
          const out = await ctx.api.patch(`${rowsPath}/${enc(cell.id)}`, { set: { [col]: parsed.value } });
          if (destroyed || layer.closed || ed.done) return;
          const i = page.rows.findIndex((r) => r._id === cell.id);
          if (i >= 0) page.rows[i] = out?.row && typeof out.row === 'object' ? out.row : { ...page.rows[i], [col]: parsed.value };
          said.textContent = t('adm.data.cell.saved', { col, n: cell.n });
          close({ focus: how !== 'blur' });
        } catch (ex) {
          if (destroyed || layer.closed || ed.done) return;
          ed.saving = false;
          ed.failed = raw;
          ctl.removeAttribute('aria-busy');
          cell.td.classList.remove('is-saving');
          showError(errorText(ex));
          if (ex?.status === 404) pendingRender = true; // the row is gone: the page reloads on close
          ctl.focus?.();
        }
      }

      function onKey(e) {
        if (e.key === 'Escape') {
          e.preventDefault?.();
          e.stopPropagation?.();
          if (!ed.saving) cancel();
        } else if (e.key === 'Enter' && !(ctl.tagName === 'TEXTAREA' && e.shiftKey)) {
          e.preventDefault?.();
          save('enter');
        }
      }
      // Escape is caught before the drawer's own handler (which would close the drawer).
      function onWinKey(e) {
        if (e.key === 'Escape' && e.target === ctl) onKey(e);
      }

      ctl.addEventListener('keydown', onKey);
      ctl.addEventListener('blur', () => { save('blur'); });
      if (kind === 'boolean') ctl.addEventListener('change', () => { save('change'); });
      win?.addEventListener?.('keydown', onWinKey, true);
      // Keep the column's width while the field is open (a field has its own width otherwise).
      const width = cell.td.offsetWidth;
      if (Number.isFinite(width) && width > 0) cell.td.style.setProperty('--cell-w', `${width}px`);
      cell.td.classList.add('is-editing');
      cell.td.removeAttribute('title');
      cell.td.replaceChildren(ctl, err);
      ctl.focus?.();
      if (kind !== 'boolean') ctl.select?.();
      return ed;
    }

    // --- « Ajouter une ligne » ------------------------------------------------------------------
    function openAddRow() {
      if (!page) return;
      addLayer?.close?.('replace');
      const cols = previewColumns(page);
      const key = page.key || tb.key || null;
      const described = new Map((Array.isArray(page.columns) ? page.columns : [])
        .filter((c) => c && typeof c === 'object' && typeof c.name === 'string').map((c) => [c.name, c]));
      const colTypes = columnTypes(page);
      const fields = cols.map((col) => {
        const type = colTypes.get(col);
        const kind = cellKind(type, undefined, page.rows, col);
        let ctl;
        if (kind === 'boolean') {
          ctl = select([['', t('adm.data.bool.empty')], ['true', t('adm.data.yes')], ['false', t('adm.data.no')]], { value: '' });
        } else {
          ctl = input({ value: '', autocomplete: 'off', inputmode: kind === 'number' ? 'decimal' : null, maxlength: TEXT_MAX, required: col === key });
        }
        const hint = [
          col === key ? t('adm.data.addRow.keyHint') : null,
          t(`adm.data.type.${kind}`),
          described.get(col)?.description || null,
        ].filter(Boolean).join(' · ');
        const f = field(col, ctl, { hint, optional: col !== key });
        return { col, type, kind, ctl, f };
      });
      const err = formError();
      const formId = aid('form');
      let modal = null;
      const submitBtn = button(t('adm.data.addRow.submit'), { variant: 'primary', type: 'submit', disabled: !fields.length });
      submitBtn.setAttribute('form', formId);
      const cancelBtn = button(t('common.cancel'), { variant: 'ghost', onClick: () => modal?.close('cancel') });

      async function submit(e) {
        e?.preventDefault?.();
        if (submitBtn.disabled) return;
        err.set(null);
        const values = {};
        let firstBad = null;
        for (const x of fields) {
          const parsed = parseCell(x.ctl.value, x.kind, x.type);
          let msg = parsed.error || null;
          if (!msg && x.col === key && parsed.value === null) msg = t('common.required');
          x.f.setError(msg);
          if (msg) firstBad ??= x.ctl;
          else if (parsed.value !== null) values[x.col] = parsed.value;
        }
        if (firstBad) {
          firstBad.focus?.();
          return;
        }
        if (!Object.keys(values).length) {
          err.set(t('adm.data.addRow.empty'));
          return;
        }
        setBusy(submitBtn, true);
        try {
          await ctx.api.post(rowsPath, { rows: [values] });
          modal?.close('api');
          if (destroyed) return;
          ctx.toast.success(t('adm.data.rowAdded'));
          if (!layer.closed) {
            // The new row comes last: show the page that holds it.
            const total = Number.isFinite(page?.total) ? page.total : (page?.rows.length ?? 0);
            const last = Math.floor(total / PREVIEW_ROWS) * PREVIEW_ROWS;
            const moved = last !== offset;
            if (moved) {
              offset = last;
              selected.clear();
            }
            loadPage({ keepScroll: !moved });
          }
          load({ quiet: true });
        } catch (ex) {
          if (modal?.closed) return;
          setBusy(submitBtn, false);
          err.set(errorText(ex));
        }
      }

      const form = h('form', { id: formId, class: 'stack adm-form adm-data-add', novalidate: true, onSubmit: submit },
        fields.length ? fields.map((x) => x.f) : h('p', { class: 'muted' }, t('adm.data.addRow.noColumns')),
        err);
      modal = ctx.overlay.openModal({
        title: t('adm.data.addRow.title', { table: tb.name }), subtitle: floor.name, size: 'md', content: form,
        footer: [cancelBtn, submitBtn], className: 'adm-modal',
      });
      addLayer = modal;
      modal.onClose?.(() => {
        if (addLayer === modal) addLayer = null;
        if (!layer.closed) addBtn.focus?.();
      });
      fields[0]?.ctl.focus?.();
      return modal;
    }

    async function deleteSelected() {
      const ids = [...selected];
      if (!ids.length) return;
      const ok = await ctx.overlay.confirm({
        title: t('adm.data.deleteRowsTitle', { n: ids.length, table: tb.name }), message: t('adm.data.deleteRowsText'),
        confirmLabel: t('common.delete'), danger: true,
      });
      if (!ok || destroyed || layer.closed) return;
      setBusy(deleteBtn, true);
      try {
        const out = await ctx.api.post(`/api/tables/${enc(floor.id)}/${enc(tb.name)}/delete-rows`, { ids });
        if (destroyed) return;
        const n = Number.isFinite(out?.deleted) ? out.deleted : ids.length;
        for (const id of ids) selected.delete(id);
        ctx.toast.success(t('adm.data.rowsDeleted', { n }));
      } catch (err) {
        if (!destroyed) ctx.toast.error(errorText(err));
      } finally {
        if (!destroyed) {
          setBusy(deleteBtn, false);
          if (!layer.closed) loadPage({ keepScroll: true });
          load({ quiet: true });
        }
      }
    }

    loadPage();
    return layer;
  }

  // --- « Nouvelle table » (on the floor shown) --------------------------------------------------
  function openNewTable() {
    const floor = currentFloor();
    if (!floor || floor.dataFloor) return null;
    newLayer?.close?.('replace');
    const nameInput = input({ value: '', maxlength: 60, autocomplete: 'off', autocapitalize: 'none', spellcheck: false, placeholder: 'concurrents-us', autofocus: true });
    const fName = field(t('adm.data.new.name'), nameInput, { hint: t('adm.data.new.nameHint') });
    const descInput = textarea({ value: '', rows: 3, maxlength: DESCRIPTION_MAX });
    const fDesc = field(t('adm.data.new.description'), descInput, { hint: t('adm.data.new.descriptionHint') });
    const keySel = select([['', t('adm.data.new.keyNone')]], { value: '' });
    const fKey = field(t('adm.data.new.key'), keySel, { hint: t('adm.data.new.keyHint'), optional: true });
    const colList = h('ol', { class: 'adm-data-newcols' });
    const colsError = h('p', { class: 'field-error', role: 'alert', hidden: true });
    const columns = []; // [{name: input, type: select, li}]
    const typeOptions = ['text', 'number', 'boolean'].map((k) => [k, t(`adm.data.type.${k}`)]);

    /** The key select lists the declared columns (the choice is kept while it still exists). */
    function syncKeys() {
      const current = keySel.value;
      const names = [...new Set(columns.map((c) => c.name.value.trim()).filter((n) => COLUMN_NAME_RE.test(n)))];
      keySel.replaceChildren(h('option', { value: '' }, t('adm.data.new.keyNone')), ...names.map((n) => h('option', { value: n }, n)));
      keySel.value = names.includes(current) ? current : '';
    }

    function renumber() {
      columns.forEach((c, i) => {
        c.name.setAttribute('aria-label', t('adm.data.new.column', { n: i + 1 }));
        c.type.setAttribute('aria-label', t('adm.data.new.columnType', { n: i + 1 }));
        c.remove.setAttribute('aria-label', t('adm.data.new.columnRemove', { n: i + 1 }));
      });
      addColBtn.disabled = columns.length >= COLUMNS_MAX;
    }

    function addColumn({ focus = true } = {}) {
      if (columns.length >= COLUMNS_MAX) return;
      const col = {
        name: input({ value: '', maxlength: 63, autocomplete: 'off', spellcheck: false, class: 'input-sm', placeholder: 'site' }),
        type: select(typeOptions, { value: 'text', class: 'input-sm' }),
      };
      col.remove = iconButton('close', t('adm.data.new.columnRemove', { n: columns.length + 1 }), {
        variant: 'ghost',
        onClick: () => {
          const i = columns.indexOf(col);
          if (i >= 0) columns.splice(i, 1);
          col.li.remove();
          renumber();
          syncKeys();
          (columns[i] || columns[i - 1])?.name.focus?.();
          if (!columns.length) addColBtn.focus?.();
        },
      });
      col.name.addEventListener('input', () => syncKeys());
      col.li = h('li', { class: 'adm-data-newcol' }, col.name, col.type, col.remove);
      columns.push(col);
      colList.appendChild(col.li);
      renumber();
      if (focus) col.name.focus?.();
    }

    const addColBtn = button(t('adm.data.new.columnAdd'), { variant: 'ghost', size: 'sm', icon: 'plus', onClick: () => addColumn() });
    const colsBox = h('fieldset', { class: 'adm-fieldset field adm-data-newcols-box' },
      h('legend', { class: 'field-label' }, t('adm.data.new.columns'), h('span', { class: 'field-optional' }, t('common.optional'))),
      h('p', { class: 'field-hint' }, t('adm.data.new.columnsHint')),
      colList, addColBtn, colsError);
    const err = formError();
    const formId = aid('form');
    let modal = null;
    const submitBtn = button(t('adm.data.new.submit'), { variant: 'primary', type: 'submit' });
    submitBtn.setAttribute('form', formId);
    const cancelBtn = button(t('common.cancel'), { variant: 'ghost', onClick: () => modal?.close('cancel') });

    async function submit(e) {
      e?.preventDefault?.();
      if (submitBtn.disabled) return;
      err.set(null);
      const { body, errors } = newTableBody({
        table: nameInput.value, description: descInput.value, key: keySel.value,
        columns: columns.map((c) => ({ name: c.name.value, type: c.type.value })),
      }, { taken: floor.tables.map((x) => x.name) });
      fName.setError(errors.table || null);
      fDesc.setError(errors.description || null);
      fKey.setError(errors.key || null);
      colsError.textContent = errors.columns || '';
      colsError.hidden = !errors.columns;
      if (!body) {
        (errors.table ? nameInput : errors.description ? descInput : errors.columns ? columns[0]?.name : keySel)?.focus?.();
        return;
      }
      setBusy(submitBtn, true);
      try {
        const created = await ctx.api.post(`/api/tables/${enc(floor.id)}`, body);
        modal?.close('api');
        if (destroyed) return;
        const info = created && typeof created === 'object' && created.name ? created : { name: body.table, description: body.description, key: body.key || null, rows: 0 };
        data = data ? {
          ...data,
          floors: data.floors.map((f) => (f.id === floor.id
            ? { ...f, tables: [...f.tables.filter((x) => x.name !== info.name), info].sort((a, b) => a.name.localeCompare(b.name)) } : f)),
        } : data;
        render();
        ctx.toast.success(t('adm.data.new.created', { name: info.name }));
        load({ quiet: true });
        // Straight into the new table: the next step is usually its first row.
        openPreview(currentFloor() || floor, info);
      } catch (ex) {
        if (modal?.closed) return;
        setBusy(submitBtn, false);
        err.set(errorText(ex));
      }
    }

    const form = h('form', { id: formId, class: 'stack adm-form adm-data-new', novalidate: true, onSubmit: submit },
      fName, fDesc, colsBox, fKey, err);
    modal = ctx.overlay.openModal({
      title: t('adm.data.new.title'), subtitle: t('adm.data.new.subtitle', { floor: floor.name }), size: 'md', content: form,
      footer: [cancelBtn, submitBtn], className: 'adm-modal',
    });
    newLayer = modal;
    modal.onClose?.(() => { if (newLayer === modal) newLayer = null; });
    nameInput.focus?.();
    return modal;
  }

  // --- drop a table (its name typed to confirm) -------------------------------------------------
  function openDrop(floor, tb) {
    dropLayer?.close?.('replace');
    const name = tb.name;
    const confirmInput = input({ value: '', placeholder: name, autocomplete: 'off', autocapitalize: 'none', spellcheck: false, autofocus: true });
    const fConfirm = field(t('adm.data.dropConfirmLabel'), confirmInput, { hint: t('adm.data.dropConfirmHint', { name }) });
    const err = formError();
    const formId = aid('form');
    let layer = null;
    const submitBtn = button(t('adm.data.dropOk'), { variant: 'danger', type: 'submit' });
    submitBtn.setAttribute('form', formId);
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('cancel') });

    async function submit(e) {
      e?.preventDefault?.();
      if (submitBtn.disabled) return;
      if (confirmInput.value.trim() !== name) {
        fConfirm.setError(t('adm.data.dropMismatch'));
        confirmInput.focus?.();
        return;
      }
      fConfirm.setError(null);
      err.set(null);
      setBusy(submitBtn, true);
      try {
        const out = await ctx.api.del(`/api/tables/${enc(floor.id)}/${enc(name)}`);
        layer?.close('api');
        if (destroyed) return;
        const n = Number.isFinite(out?.rows) ? out.rows : (tb.rows ?? 0);
        if (preview && preview.floorId === floor.id && preview.table === name) preview.layer.close('removed');
        data = data ? { ...data, floors: data.floors.map((f) => (f.id === floor.id ? { ...f, tables: f.tables.filter((x) => x.name !== name) } : f)) } : data;
        render();
        ctx.toast.success(t('adm.data.dropped', { name, n }));
        load({ quiet: true });
      } catch (ex) {
        if (layer?.closed) return;
        setBusy(submitBtn, false);
        err.set(errorText(ex));
      }
    }

    const form = h('form', { id: formId, class: 'stack adm-form', novalidate: true, onSubmit: submit },
      h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('alert', { size: 16 }),
        h('span', {}, t('adm.data.dropText', { rows: t('adm.data.rows', { n: formatNumber(tb.rows ?? 0), count: tb.rows ?? 0 }) }))),
      fConfirm, err);
    layer = ctx.overlay.openModal({
      title: t('adm.data.dropTitle', { name }), subtitle: floor.name, size: 'sm', content: form,
      footer: [cancel, submitBtn], className: 'adm-modal',
    });
    dropLayer = layer;
    layer.onClose?.(() => { if (dropLayer === layer) dropLayer = null; });
    return layer;
  }

  // Relative times drift: refresh the <time> texts every minute (focus and scroll stay put).
  const clock = setInterval(() => {
    if (destroyed || !data) return;
    const now = Date.now();
    for (const tm of body.querySelectorAll('time')) tm.textContent = relativeTime(tm.getAttribute('datetime'), now);
  }, CLOCK_MS);
  const offs = [
    // An agent (or Claude through the connector) wrote to a table: reload quietly.
    ctx.events.on('tables', () => scheduleReload()),
    // A floor created, renamed or deleted.
    ctx.store.on('office', () => { if (data) scheduleReload(); }),
  ];
  load();
  return {
    el,
    reload: () => load({ quiet: true }),
    destroy() {
      destroyed = true;
      clearInterval(clock);
      clearTimeout(reloadTimer);
      reloadTimer = null;
      for (const off of offs) off();
      addLayer?.close?.('route');
      newLayer?.close?.('route');
      preview?.layer?.close?.('route');
      dropLayer?.close?.('route');
      preview = null;
      dropLayer = null;
      addLayer = null;
      newLayer = null;
    },
  };
}
